/**
 * The user's enabled modules, mirrored into a cookie.
 *
 * The canonical source of truth is the `user_module_preferences` DB row, but
 * middleware needs to know the enabled set on every navigation to enforce the
 * disabled-app guard (see src/shared/lib/supabase/middleware.ts). Reading a
 * cookie avoids a DB round-trip on the hot path: the client rewrites it whenever
 * the user toggles a module, and middleware refreshes it from the DB the first
 * time it's missing.
 *
 * This module is deliberately free of registry / React imports so the redirect
 * decision can be unit-tested in isolation (and imported into the edge
 * middleware runtime cheaply).
 */

import { devSlotSuffix } from '@/shared/lib/devSlot'

// In per-worktree dev isolation, suffix this per-user cookie with the slot
// (e.g. `enabled_modules_s3`) so app-enablement doesn't bleed across the
// concurrent localhost previews that share one cookie jar (see devSlot.ts).
// The suffix is empty in production / the main checkout, so the name there is
// the unchanged `enabled_modules`. Every reader/writer imports this single
// constant, so browser, middleware, and the dashboard layout stay in agreement.
export const ENABLED_MODULES_COOKIE = `enabled_modules${devSlotSuffix()}`

// One year. The cookie is just a cache of a rarely-changing preference; it's
// rewritten on every change and refreshed whenever it's absent.
export const ENABLED_MODULES_COOKIE_OPTIONS = {
  path: '/',
  maxAge: 60 * 60 * 24 * 365,
  sameSite: 'lax' as const,
}

/** Encode enabled ids as a compact, comma-separated cookie value. */
export function serializeEnabledModules(ids: string[]): string {
  return ids.join(',')
}

/**
 * Decode the cookie value back into a list of ids. Returns `null` when the
 * cookie is absent so callers can distinguish "we don't know the enabled set
 * yet" (fail open) from "the user has zero modules enabled" (an empty list).
 */
export function parseEnabledModules(value: string | null | undefined): string[] | null {
  if (value == null) return null
  const trimmed = value.trim()
  if (trimmed === '') return []
  return trimmed
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
}

/**
 * The app id targeted by an `/apps/<id>/...` request, or `null` when the path is
 * not an app route. The id is taken straight from the first path segment — every
 * app route is `/apps/<id>`, and the enabled-set membership check below is what
 * decides whether it's allowed, so retired/unknown segments resolve naturally to
 * "not enabled".
 */
export function appIdForPath(pathname: string): string | null {
  const match = pathname.match(/^\/apps\/([^/]+)/)
  return match ? match[1] : null
}

/**
 * Whether a request should be bounced to the dashboard because it targets an app
 * the user has disabled (or that's retired/unknown). `enabledModuleIds` is
 * `null` when unknown (no cookie yet) — fail open and let the request through.
 */
export function shouldRedirectDisabledApp(
  pathname: string,
  enabledModuleIds: string[] | null
): boolean {
  if (enabledModuleIds == null) return false
  const appId = appIdForPath(pathname)
  return appId != null && !enabledModuleIds.includes(appId)
}

/**
 * Client-only: persist the enabled ids to the cookie so the next navigation's
 * middleware run sees the current selection without a DB read. No-op on the
 * server (no `document`).
 */
export function writeEnabledModulesCookie(ids: string[]): void {
  if (typeof document === 'undefined') return
  const { path, maxAge, sameSite } = ENABLED_MODULES_COOKIE_OPTIONS
  document.cookie = `${ENABLED_MODULES_COOKIE}=${serializeEnabledModules(
    ids
  )}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}`
}

/** Client-only: drop the cookie (e.g. on sign-out) so it can't leak to the next account. */
export function clearEnabledModulesCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${ENABLED_MODULES_COOKIE}=; path=${ENABLED_MODULES_COOKIE_OPTIONS.path}; max-age=0; SameSite=Lax`
}
